import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Clock, MapPin, ArrowUpRight, Loader2 } from "lucide-react";
import { useSeo, breadcrumbLd } from "@/lib/seo";
import { SITE_URL } from "@/data/site";
import { usePublicGet, readMin, ZONES } from "@/lib/blog";
import { PageHero, CtaBand, SisterLinks } from "@/components/public/Blocks";

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${active ? "border-[#1E2422] bg-[#1E2422] text-white" : "border-black/10 bg-white hover:border-[#C06014] hover:text-[#C06014]"}`}>
    {children}
  </button>
);

export function PostCard({ p, big = false }) {
  return (
    <Link to={`/blog/${p.slug}`} className={`card-hover group flex flex-col overflow-hidden rounded-2xl border bg-white ${big ? "md:col-span-2 md:flex-row" : ""}`} data-testid={`blog-card-${p.slug}`}>
      <div className={`relative overflow-hidden ${big ? "md:w-1/2" : ""}`}>
        <img src={p.image} alt={p.h1} loading="lazy" className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${big ? "aspect-[16/10] md:h-full" : "aspect-[16/10]"}`} />
        <span className="absolute left-3 top-3 rounded-full bg-white/95 px-3 py-1 text-xs font-bold text-[#C06014]">{p.category}</span>
      </div>
      <div className={`flex flex-1 flex-col p-6 ${big ? "md:p-10" : ""}`}>
        <p className="flex flex-wrap items-center gap-3 text-xs font-semibold text-muted-foreground">
          <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{p.zone}</span>
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{readMin(p)} min</span>
        </p>
        <h2 className={`mt-3 font-black leading-tight tracking-tight group-hover:text-[#C06014] ${big ? "text-2xl md:text-3xl" : "text-lg"}`}>{p.h1}</h2>
        <p className={`mt-3 text-sm leading-relaxed text-muted-foreground ${big ? "" : "line-clamp-3"}`}>{p.excerpt}</p>
        <span className="mt-auto flex items-center gap-1 pt-5 text-sm font-bold text-[#C06014]">Lire l'article <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" /></span>
      </div>
    </Link>
  );
}

export function BlogTeaser({ category, title = "Nos derniers conseils d'experts" }) {
  const posts = (usePublicGet("/blog").data || []).filter((p) => !category || p.category === category);
  if (!posts.length) return null;
  return (
    <section className="container-x my-16" data-testid="blog-teaser">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><p className="text-xs font-bold uppercase tracking-widest text-[#53634F]">Le blog</p><h2 className="mt-2 text-2xl font-black tracking-tight md:text-3xl">{title}</h2></div>
        <Link to="/blog" className="font-semibold text-[#C06014] hover:underline" data-testid="blog-teaser-all">Tous les articles →</Link>
      </div>
      <div className="mt-6 grid gap-6 md:grid-cols-3">{posts.slice(0, 3).map((p) => <PostCard key={p.slug} p={p} />)}</div>
    </section>
  );
}

export default function Blog() {
  const { data, loading } = usePublicGet("/blog");
  const posts = useMemo(() => data || [], [data]);
  const [zone, setZone] = useState("");
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(23);
  useEffect(() => { setLimit(23); }, [zone, cat, q]);
  const cats = [...new Set(posts.map((p) => p.category))];
  const shown = posts.filter((p) => (!zone || p.zone === zone) && (!cat || p.category === cat)
    && (!q || `${p.h1} ${p.excerpt} ${(p.keywords || []).join(" ")}`.toLowerCase().includes(q.toLowerCase())));
  const filtered = zone || cat || q;
  useSeo({
    title: "Blog termites, xylophages, moustiques & humidité | Termite Habitat",
    description: "Conseils d'experts sur les termites, capricornes, vrillettes, moustiques et l'humidité : Bordeaux, Gironde, Nouvelle-Aquitaine et toute la France.",
    path: "/blog",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        breadcrumbLd([{ name: "Accueil", path: "/" }, { name: "Blog", path: "/blog" }]),
        { "@type": "Blog", "@id": `${SITE_URL}/blog#blog`, name: "Le blog TERMITE HABITAT", inLanguage: "fr-FR", publisher: { "@id": `${SITE_URL}/#org` },
          blogPost: posts.slice(0, 50).map((p) => ({ "@type": "BlogPosting", headline: p.h1, url: `${SITE_URL}/blog/${p.slug}`, datePublished: p.date, image: `${SITE_URL}${p.image}` })) },
      ],
    },
  });
  return (
    <>
      <PageHero kicker="Le blog" h1="Termites, xylophages, moustiques & humidité : le blog des experts" image="/images/bois.webp"
        intro="Reconnaître les signes, comprendre les traitements, connaître vos obligations : nos conseils pour Bordeaux, la Gironde, la Nouvelle-Aquitaine et toute la France."
        crumbs={[{ name: "Accueil", path: "/" }, { name: "Blog", path: "/blog" }]} />
      <section className="container-x py-12">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" data-testid="blog-zone-filters">
            <Chip active={!zone} onClick={() => setZone("")} testid="blog-zone-all">Toutes les zones</Chip>
            {ZONES.map((z) => <Chip key={z} active={zone === z} onClick={() => setZone(z)} testid={`blog-zone-${z}`}>{z}</Chip>)}
          </div>
          <label className="relative w-full lg:w-80">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un sujet…" data-testid="blog-search-input"
              className="w-full rounded-full border bg-white py-2.5 pl-11 pr-4 text-sm outline-none focus:border-[#C06014]" />
          </label>
        </div>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="blog-category-filters">
          <Chip active={!cat} onClick={() => setCat("")} testid="blog-cat-all">Tous les sujets</Chip>
          {cats.map((c) => <Chip key={c} active={cat === c} onClick={() => setCat(c)} testid={`blog-cat-${c}`}>{c}</Chip>)}
        </div>
        {loading && <p className="mt-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#C06014]" /></p>}
        {!loading && !shown.length && <p className="mt-16 rounded-2xl border border-dashed bg-white p-10 text-center text-sm text-muted-foreground" data-testid="blog-empty">Aucun article ne correspond à votre recherche.</p>}
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="blog-list">
          {shown.slice(0, limit).map((p, i) => <PostCard key={p.slug} p={p} big={!filtered && i === 0} />)}
        </div>
        {shown.length > limit && <p className="mt-10 text-center"><button onClick={() => setLimit(limit + 24)} className="rounded-full border bg-white px-6 py-3 text-sm font-bold hover:border-[#C06014] hover:text-[#C06014]" data-testid="blog-load-more">Voir plus d'articles ({shown.length - limit})</button></p>}
      </section>
      <CtaBand />
      <SisterLinks />
    </>
  );
}
